import { useEffect, useState } from "react";
import { CheckCircle2, RotateCcw, Volume2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import GameShell, { shuffle, pickWeighted } from "./GameShell";

/**
 * Segmenting Game — the phonological-awareness inverse of Blending.
 *
 * The learner hears a whole word (e.g. "cat"), then must pull it apart
 * into its phonemes in order by tapping tiles from a shuffled bank that
 * contains both the real phonemes AND close-sounding distractors.
 *
 * This closes the read-write loop: Blending trains synthesis (sounds → word),
 * Segmenting trains analysis (word → sounds). Both are core to the Science
 * of Reading.
 */
export default function SegmentingGame() {
    const [items, setItems] = useState([]);
    const [level, setLevel] = useState(1);
    const [round, setRound] = useState(null);
    const [placed, setPlaced] = useState([]); // graphemes the learner has picked, in order
    const [bank, setBank] = useState([]);     // remaining shuffled bank
    const [correct, setCorrect] = useState(0);
    const [total, setTotal] = useState(0);
    const [done, setDone] = useState(false);
    const [feedback, setFeedback] = useState(null); // "correct" | "wrong" | null
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/games/segmenting").then((r) => {
            setItems(r.data.items);
            setLevel(r.data.level);
            newRound(r.data.items);
        });
    }, []);

    const newRound = (source) => {
        const target = pickWeighted(source) || source[0];
        const combined = shuffle([...target.graphemes, ...target.distractors]);
        setRound(target);
        setPlaced([]);
        setBank(combined);
        setFeedback(null);
        // Auto-play the word once so the learner hears what to segment.
        setTimeout(() => playWord(target.word), 250);
    };

    const playWord = async (word) => {
        if (busy) return;
        setBusy(true);
        try {
            const { data } = await api.post("/tts", { text: word, mode: "text" });
            await new Audio(`data:${data.mime};base64,${data.audio_base64}`).play().catch(() => {});
        } catch {
            /* noop */
        } finally {
            setBusy(false);
        }
    };

    const playSoundOut = async (word) => {
        if (busy) return;
        setBusy(true);
        try {
            const { data } = await api.post("/tts", { text: word, mode: "sound_out" });
            await new Audio(`data:${data.mime};base64,${data.audio_base64}`).play().catch(() => {});
        } catch {
            /* noop */
        } finally {
            setBusy(false);
        }
    };

    const pickBankTile = (g, i) => {
        if (feedback) return;
        setPlaced((p) => [...p, g]);
        setBank((b) => b.filter((_, idx) => idx !== i));
    };

    const removePlaced = (i) => {
        if (feedback) return;
        const g = placed[i];
        setPlaced((p) => p.filter((_, idx) => idx !== i));
        setBank((b) => [...b, g]);
    };

    const check = async () => {
        if (!round || feedback) return;
        const ok =
            placed.length === round.graphemes.length &&
            placed.every((g, i) => g === round.graphemes[i]);
        setFeedback(ok ? "correct" : "wrong");
        const newTotal = total + 1;
        const newCorrect = correct + (ok ? 1 : 0);
        setTotal(newTotal);
        setCorrect(newCorrect);
        if (ok) {
            toast.success(`Perfect — ${round.word} has ${round.graphemes.length} sounds`);
        } else {
            toast.error(`Not quite — listen again and try once more`);
            // Adaptive difficulty: log each grapheme the learner placed wrong
            // so future rounds surface tough phonemes more often.
            round.graphemes.forEach((expected, i) => {
                if (placed[i] !== expected) {
                    api.post("/games/grapheme-miss", { grapheme: expected }).catch(() => {});
                }
            });
        }
        setTimeout(() => {
            if (newTotal >= 10) {
                finish(newCorrect);
            } else {
                newRound(items);
            }
        }, ok ? 1100 : 1500);
    };

    const finish = async (finalCorrect) => {
        setDone(true);
        try {
            await api.post("/progress/game-score", {
                game: "segmenting",
                score: finalCorrect * 6,
                correct: finalCorrect,
                total: 10,
            });
            toast.success(`+${finalCorrect * 6} XP earned`);
        } catch {
            /* non-fatal */
        }
    };

    const reset = () => {
        setCorrect(0); setTotal(0); setDone(false); newRound(items);
    };

    if (!round)
        return (
            <GameShell title="Segmenting">
                <p>Loading…</p>
            </GameShell>
        );

    // Placement slots — always exactly N slots (matches the target).
    const slotCount = round.graphemes.length;
    const placeCls = (i) => {
        if (feedback === "correct") return "tile tile-correct";
        if (feedback === "wrong" && placed[i] && placed[i] !== round.graphemes[i]) return "tile tile-wrong";
        return "tile";
    };

    return (
        <GameShell
            title="Segmenting"
            intro="Listen to the word, then break it into its sounds."
            testId="segmenting-game"
            instructions="This is the Segmenting game. You will hear a word out loud %%[data-testid='segmenting-word']%%. Below the word, you will see empty slots — one for each sound in the word %%[data-testid='segmenting-slots']%%. Under the slots is a bank of sound tiles %%[data-testid='segmenting-bank']%%. Tap the sound tiles in order to fill the empty slots. Some tiles are extras that you should skip. When every slot is filled, tap the Check button %%[data-testid='segmenting-check']%%. Ten rounds total."
        >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
                <div className="flex items-center gap-3">
                    <p className="text-sm text-[#819888]">Round {done ? total : total + 1} of 10</p>
                    <span
                        className="text-xs px-2 py-1 rounded font-medium"
                        style={{
                            background: level === 2 ? "#FDE9DD" : "#E0EDE6",
                            color: level === 2 ? "#B86644" : "#2D5D4E",
                        }}
                        data-testid="segmenting-level-badge"
                    >
                        Level {level} · {level === 2 ? "Vowel teams" : "CVC"}
                    </span>
                </div>
                <p className="text-sm text-[#2D5D4E] font-medium" data-testid="segmenting-score">
                    Score: {correct} / {total}
                </p>
            </div>

            {done ? (
                <div className="card-rw p-10 text-center">
                    <CheckCircle2 className="w-12 h-12 text-[#3A7D5C] mx-auto mb-4" strokeWidth={1.6} />
                    <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>
                        Round complete
                    </h2>
                    <p className="text-lg text-[#515B57] mb-6">
                        You segmented {correct} out of {total} words correctly.
                    </p>
                    <button
                        onClick={reset}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                        data-testid="segmenting-replay"
                    >
                        <RotateCcw className="w-4 h-4" /> Play again
                    </button>
                </div>
            ) : (
                <>
                    {/* Word card + replay button */}
                    <div className="card-rw p-8 md:p-10 mb-6 text-center" data-testid="segmenting-word">
                        <p className="label-eyebrow mb-3">Listen to this word</p>
                        <p
                            className="text-5xl md:text-6xl font-semibold mb-6 text-[#1C2321]"
                            style={{ fontFamily: "Outfit" }}
                        >
                            {round.word}
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            <button
                                onClick={() => playWord(round.word)}
                                disabled={busy}
                                data-testid="segmenting-hear-word"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[#E5E7E2] bg-white text-[#2D5D4E] font-medium hover:bg-[#E8E2D2] disabled:opacity-60"
                            >
                                {busy ? (
                                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
                                ) : (
                                    <Volume2 className="w-4 h-4" strokeWidth={1.8} />
                                )}
                                Hear the word
                            </button>
                            <button
                                onClick={() => playSoundOut(round.word)}
                                disabled={busy}
                                data-testid="segmenting-hear-sounds"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[#E5E7E2] bg-white text-[#2D5D4E] font-medium hover:bg-[#E8E2D2] disabled:opacity-60"
                            >
                                {busy ? (
                                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
                                ) : (
                                    <Volume2 className="w-4 h-4" strokeWidth={1.8} />
                                )}
                                Sound it out
                            </button>
                        </div>
                    </div>

                    {/* Placement slots */}
                    <p className="label-eyebrow mb-3 text-center">
                        Tap the sounds in order
                    </p>
                    <div
                        className="flex items-center justify-center gap-3 mb-8 flex-wrap min-h-[96px]"
                        data-testid="segmenting-slots"
                    >
                        {Array.from({ length: slotCount }).map((_, i) => {
                            const g = placed[i];
                            if (g) {
                                return (
                                    <button
                                        key={i}
                                        onClick={() => removePlaced(i)}
                                        disabled={!!feedback}
                                        data-testid={`segmenting-slot-${i}`}
                                        aria-label={`Remove ${g}`}
                                        className={`${placeCls(i)} w-20 h-20 md:w-24 md:h-24 text-3xl md:text-4xl relative`}
                                        style={{ fontFamily: "Outfit" }}
                                    >
                                        <span>{g.replace("_", "-")}</span>
                                        {!feedback && (
                                            <X
                                                className="absolute top-1.5 right-1.5 w-3.5 h-3.5 text-[#819888]"
                                                strokeWidth={2}
                                            />
                                        )}
                                    </button>
                                );
                            }
                            return (
                                <div
                                    key={i}
                                    data-testid={`segmenting-slot-${i}`}
                                    data-empty="true"
                                    className="w-20 h-20 md:w-24 md:h-24 rounded-lg border-2 border-dashed border-[#C6CFC9] flex items-center justify-center text-[#819888] text-xl"
                                >
                                    {i + 1}
                                </div>
                            );
                        })}
                    </div>

                    {/* Grapheme bank */}
                    <p className="label-eyebrow mb-3 text-center">Available sounds</p>
                    <div
                        className="flex items-center justify-center gap-3 mb-8 flex-wrap"
                        data-testid="segmenting-bank"
                    >
                        {bank.map((g, i) => (
                            <button
                                key={`${g}-${i}`}
                                onClick={() => pickBankTile(g, i)}
                                disabled={!!feedback}
                                data-testid={`segmenting-bank-${i}`}
                                className="tile w-16 h-16 md:w-20 md:h-20 text-2xl md:text-3xl"
                                style={{ fontFamily: "Outfit" }}
                            >
                                {g.replace("_", "-")}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={check}
                        disabled={placed.length !== slotCount || !!feedback}
                        data-testid="segmenting-check"
                        className="w-full py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237] disabled:opacity-50"
                    >
                        Check
                    </button>
                </>
            )}
        </GameShell>
    );
}
